import cx from 'classnames';
import {priority} from '@priority-software/priority-proto-api/lib';
import {useQuery} from 'react-query';
import React, {useState} from 'react';
import {WidgetHeader} from './WidgetHeader';
import {ToDoListContent} from './ToDoListContent';
import {
    fecthToDosData,
    ToDoListData,
    getSortedRecords,
    getFormStatuses,
    updateTaskPriority,
    updateTaskStatus,
    openToDoListScreen,
    saveUpdatedStatus,
    fetchWidgetData
} from '../actions';
import {
    WIDGET_ID,
    WIDGET_QUERY_KEY,
    SORT_QUERY_KEY,
    defaultCols,
    TODOLIST,
    MAX_FIELDS,
    monitor
} from '../consts';
import {
    getMandatoryColIds,
    getViewColsData,
    getAvailableCols,
    getWidgetViewColumns,
    saveWidgetViewColumns
} from '../utils';
import {CustomQueryClientProvider} from '../../../hooks/useQueryClientProvider';
import {ISort} from '../../../stores/Form/Sort';
import {Record} from '../../../stores/Form/Record';
import {apiLocker} from '../../../stores/Form';
import {EditingDialog} from '../../../components/EditingDialog';
import styles from '../styles/WidgetHeader.module.scss';
import {sendMonitor} from '../../../utils/monitoring';
import {getDname} from '../../../_shared/GWTBridge/invoke/genPage';
import {createDataTestId} from '../../../utils/dataTestId';

type WidgetProps = {
    props: {
        ex: string;
        selector: string;
    };
    onRemove?: (exec: string, title: string) => void;
    componentClasses?: string;
};

const ToDoListWidgetComp = ({props, componentClasses, onRemove}: WidgetProps) => {
    const [records, setRecords] = useState<Record[]>([]);
    const [viewCols, setViewCols] = useState<priority.netitems.form.IColumn[]>(null);
    const [sortedCol, setSortedCol] = useState<ISort>(null);
    const [isSettingsPanelOpen, setIsSettingsPanelOpen] = useState(false);

    const {data}: {data: ToDoListData} = useQuery(
        [WIDGET_QUERY_KEY + getDname()],
        async () => {
            const selectedColumnsNames = await getWidgetViewColumns();

            return fecthToDosData(selectedColumnsNames);
        },
        {
            onSuccess(res) {
                setRecords(res?.form.records.filter(record => !record.isNew));
                return setViewCols(res?.viewColumns);
            }
        }
    );
    useQuery(
        [SORT_QUERY_KEY, sortedCol],
        () =>
            getSortedRecords({
                form: data?.form,
                filter: data?.filter,
                sortCol: sortedCol
            }),
        {
            enabled: !!sortedCol,
            onSuccess(records) {
                setRecords(records.filter(record => !record.isNew));
            }
        }
    );

    const deleteWidget = () => {
        const {ex, selector} = props;
        if (onRemove) {
            onRemove(ex, selector);
            sendMonitor(monitor.TODOLIST_Delete);
        }
    };

    const onSort = (viewCol: priority.netitems.form.IColumn) => {
        if (apiLocker.isLock(data.form.session)) {
            return;
        }
        let isDesc;
        if (!sortedCol || sortedCol.ColumnID !== viewCol.ID) {
            isDesc = {Desc: false};
        } else if (!sortedCol.Desc) {
            isDesc = {Desc: true};
        }
        setSortedCol({ColumnID: viewCol.ID, ...isDesc});
    };

    const onOpenToDoListScreen = () => {
        (window as any).hide_home_page?.();
        const {form, userNameCol, userName} = data;
        openToDoListScreen({formName: form.ename, userNameColName: userNameCol.Name, userName});
        sendMonitor(monitor.TODOLIST_Expand);
    };

    const getStatusList = async (
        record: Record
    ): Promise<priority.netitems.form.ISelectValues[]> => {
        sendMonitor(monitor.TODOLIST_Status);
        const {form} = data;
        const statuses = await getFormStatuses({form, record});
        return statuses;
    };

    const onUpdateTaskPriority = async (record: Record, value: string) => {
        const {form} = data;
        await updateTaskPriority({form, record, value});
    };

    const onSaveStatusUpdate = async () => {
        await saveUpdatedStatus(data.form);
    };

    const onUpdateTaskStatus = async (value: string) => {
        const {form} = data;
        await updateTaskStatus({form, value});
    };

    const onUpdateDisplayCols = async (entityId: string, selectedFields: Array<string>) => {
        await saveWidgetViewColumns(selectedFields);
        const columns = getViewColsData(data?.form.columns, selectedFields);
        setViewCols(columns);
        const res = await fetchWidgetData(
            data.form,
            data.form.columns,
            data.userNameCol.ID as number,
            data.curDateColID,
            columns.map(col => col.Name)
        );
        data.form.setRecords(res?.response.Records);
        setRecords(data.form.records.filter(record => !record.isNew));
        setIsSettingsPanelOpen(false);
        const selectedFieldsString = selectedFields.reduce((acc, currentField) => {
            return `${acc} ${currentField}, `;
        }, 'selected fields:');
        sendMonitor(monitor.TODOLIST_FieldsDisaply, selectedFieldsString);
    };

    const onSettingsClick = (isSettingPanelOpen: boolean) => {
        setIsSettingsPanelOpen(isSettingPanelOpen);
        isSettingPanelOpen && sendMonitor(monitor.TODLIST_Settings);
    };

    return (
        <div
            id={WIDGET_ID}
            className={cx('ignore-drag', styles.widgetContainer, componentClasses)}
            {...createDataTestId('todo-list-widget')}
        >
            {data && (
                <>
                    <WidgetHeader
                        form={data.form}
                        taskCount={records.length}
                        deleteWidget={deleteWidget}
                        openToDoListScreen={onOpenToDoListScreen}
                        setIsSettingsPanelOpen={onSettingsClick}
                    />
                    {viewCols && (
                        <ToDoListContent
                            mandatoryColIds={getMandatoryColIds(data.form.columns)}
                            viewCols={viewCols}
                            records={records}
                            sortedCol={sortedCol}
                            onSort={onSort}
                            openToDoListScreen={onOpenToDoListScreen}
                            getStatusList={getStatusList}
                            saveUpdate={onSaveStatusUpdate}
                            updateTaskStatus={onUpdateTaskStatus}
                            updatePriorityField={onUpdateTaskPriority}
                            formName={data.form.title}
                        />
                    )}
                    {isSettingsPanelOpen && (
                        <EditingDialog
                            isOpen={isSettingsPanelOpen}
                            defaultFields={defaultCols}
                            selectedEntity={{
                                entityId: TODOLIST,
                                cols: getAvailableCols(data.form.columns),
                                selectedFields: viewCols?.map(col => col.Name)
                            }}
                            closeDialog={() => setIsSettingsPanelOpen(false)}
                            updateFields={onUpdateDisplayCols}
                            maxSelectableFields={MAX_FIELDS}
                        />
                    )}
                </>
            )}
        </div>
    );
};

export const ToDoListWidget = ({props, componentClasses, onRemove}: WidgetProps) => (
    <CustomQueryClientProvider>
        <ToDoListWidgetComp props={props} componentClasses={componentClasses} onRemove={onRemove} />
    </CustomQueryClientProvider>
);
